// re.rejouis.fr — primitives UI (charte Rejouis 2026).
import { fmtEuro } from '../lib/format.js';
import heartUrl from '../assets/rejouis-heart.png';

// Stickers de la charte 2026 (PNG transparents ~10 Ko, chargés à la demande par Vite).
const STICKERS = import.meta.glob('../assets/stickers/*.png', { eager: true, import: 'default' });
const stickerUrl = (nom) => STICKERS[`../assets/stickers/${nom}.png`];

// Illustration décorative : <Sticker nom="coeur-etincelle" size={64} />.
// anim : 'flotte' (léger flottement) | 'pop' (apparition) ; rot en degrés.
export function Sticker({ nom, size = 56, rot = 0, anim, style }) {
  const src = stickerUrl(nom);
  if (!src) return null;
  return (
    <img src={src} alt="" aria-hidden="true" width={size} height={size} className={'sticker' + (anim ? ' ' + anim : '')}
      style={{ width: size, height: size, '--rot': rot + 'deg', transform: anim ? undefined : `rotate(${rot}deg)`, ...style }} />
  );
}

// Fil du parcours de reprise, affiché en tête des écrans du tunnel.
const PARCOURS = ['Estimation', 'Panier', 'Vérifications', 'Envoi'];
export function Parcours({ etape }) {
  return (
    <div className="parcours" aria-label={`Étape ${etape + 1} sur ${PARCOURS.length} : ${PARCOURS[etape]}`}>
      {PARCOURS.map((l, i) => (
        <span key={l} style={{ display: 'contents' }}>
          {i > 0 ? <span className={'pc-trait' + (i <= etape ? ' fait' : '')} /> : null}
          <span className={'pc-etape' + (i < etape ? ' fait' : i === etape ? ' actif' : '')}>
            <span className="pc-num">{i < etape ? '✓' : i + 1}</span>
            <span className="pc-label">{l}</span>
          </span>
        </span>
      ))}
    </div>
  );
}

// Mini-illustrations plates (style des stickers de la charte : aplats bleu nuit,
// rouge, jaune, petites étincelles), en tons adoucis pour rester discrets. <Illus nom="photo" size={46} />
const etincelle = (cx, cy, r) => `M${cx} ${cy - r}Q${cx} ${cy} ${cx + r} ${cy}Q${cx} ${cy} ${cx} ${cy + r}Q${cx} ${cy} ${cx - r} ${cy}Q${cx} ${cy} ${cx} ${cy - r}Z`;
const ILLUS = {
  photo: (
    <>
      <rect x="10" y="9" width="14" height="8" rx="3" fill="#3A3585" />
      <rect x="3" y="14" width="36" height="26" rx="8" fill="#3A3585" />
      <circle cx="21" cy="27" r="9" fill="#BFE6FA" />
      <circle cx="21" cy="27" r="4" fill="#fff" />
      <circle cx="33" cy="20" r="2" fill="#FBE7B5" />
      <path d={etincelle(41, 8, 6)} fill="#F6CF73" />
    </>
  ),
  lot: (
    <>
      <path d="M5 17 24 9l19 8-19 8z" fill="#FBE7B5" />
      <path d="M5 17l19 8v19L5 36z" fill="#F6CF73" />
      <path d="M24 25l19-8v19l-19 8z" fill="#EFC566" />
      <path d="M14.5 13 33.5 21v6" stroke="#3A3585" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M13 32.5c-2.6-1.6-4-3.2-4-4.8 0-1.3 1-2.2 2.1-2.2.8 0 1.5.5 1.9 1.1.4-.6 1.1-1.1 1.9-1.1 1.1 0 2.1.9 2.1 2.2 0 1.6-1.4 3.2-4 4.8z" fill="#F59A9A" />
    </>
  ),
  ajout: (
    <>
      <path d="M30 30l9.5 9.5" stroke="#3A3585" strokeWidth="6.5" strokeLinecap="round" />
      <circle cx="20" cy="20" r="13" fill="var(--bleu-pale)" stroke="#3A3585" strokeWidth="4" />
      <path d="M20 14v12M14 20h12" stroke="#EE8A8A" strokeWidth="3.6" strokeLinecap="round" />
      <path d={etincelle(41, 8, 6)} fill="#F6CF73" />
    </>
  ),
  don: (
    <>
      <path d="M20 41C8.5 33.5 3.5 26.5 3.5 19.5 3.5 14 7.7 10 12.8 10c3.3 0 5.9 1.8 7.2 4.4C21.3 11.8 23.9 10 27.2 10c5.1 0 9.3 4 9.3 9.5 0 7-5 14-16.5 21.5z" fill="#F59A9A" />
      <path d={etincelle(40, 9, 6.5)} fill="#F6CF73" />
      <path d={etincelle(44, 22, 3.5)} fill="#F6CF73" />
    </>
  ),
  camion: (
    <>
      <rect x="2" y="12" width="27" height="21" rx="4" fill="#3A3585" />
      <path d="M29 18h8.5l6.5 7.5V33a2 2 0 0 1-2 2H29z" fill="#F6CF73" />
      <path d="M32 21h4.5l3.5 4.5h-8z" fill="#fff" />
      <path d="M15.5 27.2c-2.6-1.6-4-3.2-4-4.8 0-1.3 1-2.2 2.1-2.2.8 0 1.5.5 1.9 1.1.4-.6 1.1-1.1 1.9-1.1 1.1 0 2.1.9 2.1 2.2 0 1.6-1.4 3.2-4 4.8z" fill="#F59A9A" />
      <circle cx="11" cy="36" r="5" fill="#3A3585" stroke="#fff" strokeWidth="2.5" />
      <circle cx="35" cy="36" r="5" fill="#3A3585" stroke="#fff" strokeWidth="2.5" />
    </>
  ),
  bouclier: (
    <>
      <path d="M22 5 37 10.5v10.5c0 10-6.5 16.5-15 21C13.5 37.5 7 31 7 21V10.5z" fill="#BFE6FA" />
      <path d="M15 22.5l5 5 9.5-10" stroke="#3A3585" strokeWidth="3.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d={etincelle(41, 8, 6)} fill="#F6CF73" />
    </>
  ),
  verifie: (
    <>
      <path d="M30 30l9.5 9.5" stroke="#3A3585" strokeWidth="6.5" strokeLinecap="round" />
      <circle cx="20" cy="20" r="13" fill="var(--bleu-pale)" stroke="#3A3585" strokeWidth="4" />
      <path d="M14.5 20.5l4 4 7-7.5" stroke="#EE8A8A" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d={etincelle(41, 8, 6)} fill="#F6CF73" />
    </>
  ),
  mail: (
    <>
      <rect x="3" y="12" width="36" height="26" rx="6" fill="#3A3585" />
      <path d="M7 16.5 21 27l14-10.5" stroke="#BFE6FA" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M21 33.5c-2.6-1.6-4-3.2-4-4.8 0-1.3 1-2.2 2.1-2.2.8 0 1.5.5 1.9 1.1.4-.6 1.1-1.1 1.9-1.1 1.1 0 2.1.9 2.1 2.2 0 1.6-1.4 3.2-4 4.8z" fill="#F59A9A" />
      <path d={etincelle(42, 9, 6)} fill="#F6CF73" />
    </>
  ),
  bon: (
    <>
      <path d="M3 15a4 4 0 0 1 4-4h30a4 4 0 0 1 4 4v5.5a4.5 4.5 0 0 0 0 9V35a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4v-5.5a4.5 4.5 0 0 0 0-9z" fill="#F59A9A" />
      <path d="M29 13v24" stroke="#fff" strokeWidth="2" strokeDasharray="2.5 3" strokeLinecap="round" />
      <path d="M16 31c-3.6-2.2-5.6-4.5-5.6-6.7 0-1.8 1.4-3.1 3-3.1 1.1 0 2.1.6 2.6 1.5.5-.9 1.5-1.5 2.6-1.5 1.6 0 3 1.3 3 3.1 0 2.2-2 4.5-5.6 6.7z" fill="#fff" />
      <path d={etincelle(43, 8, 5.5)} fill="#F6CF73" />
    </>
  ),
  relais: (
    <>
      <rect x="7" y="19" width="32" height="22" rx="3" fill="#FBE7B5" />
      <path d="M5 13a4 4 0 0 1 4-4h28a4 4 0 0 1 4 4v6a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4z" fill="#F59A9A" />
      <path d="M14.5 9v14M23 9v14M31.5 9v14" stroke="#fff" strokeWidth="2.6" />
      <rect x="18.5" y="28" width="9" height="13" rx="2" fill="#3A3585" />
    </>
  ),
  propre: (
    <>
      <circle cx="18" cy="28" r="12" fill="#BFE6FA" />
      <circle cx="33" cy="17" r="7" fill="#BFE6FA" />
      <circle cx="36" cy="33" r="4.5" fill="#BFE6FA" />
      <path d="M11.5 24.5a7 7 0 0 1 5-4.5" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" fill="none" />
      <path d="M30 14.5a3.5 3.5 0 0 1 2.5-1.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" fill="none" />
      <path d={etincelle(42, 6, 5)} fill="#F6CF73" />
      <path d={etincelle(7, 11, 4)} fill="#F6CF73" />
    </>
  ),
  flamme: (
    <>
      <path d="M22 4c2 7.5-9 11-9 22a11 11 0 0 0 22 0c0-5-2.5-9-5.5-11.5.2 4-1.4 6.5-3.6 7.5C27.5 15.5 26 9 22 4z" fill="#F59A9A" />
      <path d="M22.5 26c1.2 3.5-3.5 4.6-3.5 8.6a4.5 4.5 0 0 0 9 0c0-2.4-1.3-4-2.6-5-.1 1.4-.7 2.2-1.6 2.6.4-2.4-.1-4.6-1.3-6.2z" fill="#F6CF73" />
      <path d={etincelle(41, 9, 5.5)} fill="#F6CF73" />
    </>
  ),
  etoile: (
    <>
      <path d="M22 5.5l5 10.6 11.5 1.5-8.4 8 2.1 11.4L22 31.4 11.8 37l2.1-11.4-8.4-8 11.5-1.5z" fill="#F6CF73" strokeLinejoin="round" />
      <circle cx="22" cy="22.5" r="3.2" fill="#3A3585" />
      <path d={etincelle(42, 8, 5)} fill="#F59A9A" />
    </>
  ),
  oups: (
    <>
      <circle cx="22" cy="25" r="17" fill="#F59A9A" />
      <path d="M22 16v11" stroke="#fff" strokeWidth="4" strokeLinecap="round" />
      <circle cx="22" cy="33" r="2.4" fill="#fff" />
      <path d={etincelle(41, 7, 5)} fill="#F6CF73" />
    </>
  ),
  // ── Réponses du questionnaire d'état ──
  marche: (
    <>
      <circle cx="22" cy="25" r="17" fill="#BFE6FA" />
      <path d="M15.5 18.5a9.5 9.5 0 1 0 13 0" stroke="#3A3585" strokeWidth="3.6" strokeLinecap="round" fill="none" />
      <path d="M22 13v12" stroke="#3A3585" strokeWidth="3.6" strokeLinecap="round" />
      <path d={etincelle(41, 8, 6)} fill="#F6CF73" />
    </>
  ),
  scelle: (
    <>
      <rect x="5" y="14" width="34" height="27" rx="4" fill="#F6CF73" />
      <rect x="5" y="14" width="34" height="8" rx="3" fill="#EFC566" />
      <path d="M22 14v27M5 27.5h34" stroke="#F59A9A" strokeWidth="4" />
      <path d="M22 14c-3-5-9-6-9-2.5S19 14 22 14c3 0 9-1 9-2.5S25 9 22 14z" fill="#F59A9A" />
      <path d={etincelle(42, 7, 5.5)} fill="#3A3585" />
    </>
  ),
  ouvert: (
    <>
      <path d="M7 20h30v17a4 4 0 0 1-4 4H11a4 4 0 0 1-4-4z" fill="#F6CF73" />
      <path d="M7 20 2 11l14-3 6 12z" fill="#FBE7B5" />
      <path d="M37 20l5-9-14-3-6 12z" fill="#EFC566" />
      <path d="M17 31c-2.6-1.6-4-3.2-4-4.8 0-1.3 1-2.2 2.1-2.2.8 0 1.5.5 1.9 1.1.4-.6 1.1-1.1 1.9-1.1 1.1 0 2.1.9 2.1 2.2 0 1.6-1.4 3.2-4 4.8z" fill="#F59A9A" />
      <rect x="23" y="26" width="9" height="4" rx="2" fill="#3A3585" />
    </>
  ),
  'traces-aucune': (
    <>
      <rect x="6" y="16" width="30" height="18" rx="9" fill="#BFE6FA" />
      <path d="M12 21a6 6 0 0 1 5-2" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" fill="none" />
      <path d={etincelle(40, 10, 6.5)} fill="#F6CF73" />
      <path d={etincelle(8, 38, 4)} fill="#F6CF73" />
    </>
  ),
  'traces-legeres': (
    <>
      <rect x="6" y="16" width="30" height="18" rx="9" fill="#BFE6FA" />
      <path d="M15 25l4-3M24 29l3.5-2.5" stroke="#3A3585" strokeWidth="2.2" strokeLinecap="round" opacity="0.8" />
      <path d={etincelle(40, 10, 5)} fill="#F6CF73" />
    </>
  ),
  'traces-marquees': (
    <>
      <rect x="6" y="16" width="30" height="18" rx="9" fill="#BFE6FA" />
      <path d="M13 28l9-8M19 30l9-8M26 29l5-5" stroke="#3A3585" strokeWidth="2.6" strokeLinecap="round" />
      <circle cx="13" cy="21" r="1.8" fill="#F59A9A" />
    </>
  ),
  complet: (
    <>
      <rect x="4" y="15" width="30" height="24" rx="4" fill="#F6CF73" />
      <path d="M4 22h30" stroke="#EFC566" strokeWidth="3" />
      <path d="M11 30c4-5 9 3 14-2" stroke="#3A3585" strokeWidth="2.6" strokeLinecap="round" fill="none" />
      <circle cx="36" cy="13" r="8" fill="#3A3585" />
      <path d="M32.5 13l2.5 2.5 4.5-5" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </>
  ),
  incomplet: (
    <>
      <rect x="4" y="15" width="30" height="24" rx="4" fill="#F6CF73" />
      <path d="M4 22h30" stroke="#EFC566" strokeWidth="3" />
      <rect x="10" y="27" width="16" height="7" rx="3.5" fill="none" stroke="#3A3585" strokeWidth="2" strokeDasharray="3 2.5" />
      <circle cx="36" cy="13" r="8" fill="#F59A9A" />
      <path d="M32.5 13h7" stroke="#fff" strokeWidth="2.8" strokeLinecap="round" />
    </>
  ),
};
export function Illus({ nom, size = 46 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" style={{ display: 'block', flexShrink: 0 }}>
      {ILLUS[nom]}
    </svg>
  );
}

// Cœur Rejouis officiel (cœur seul, fond transparent).
function CoeurRejouis({ taille = 28 }) {
  return <img src={heartUrl} alt="" width={taille} height={taille} style={{ display: 'block', flexShrink: 0 }} aria-hidden="true" />;
}

// ── Icônes (style Lucide, trait 2, bouts ronds) ──
function Ic({ d, size = 20, sw = 2, style, children }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={style} aria-hidden="true">
      {d ? <path d={d} /> : null}{children}
    </svg>
  );
}

// Largeur max de l'interface sur PC (colonne + barres fixes) ; plein écran sur mobile.
export const LARGEUR_MAX = 1040;

export const Icons = {
  search: (p) => <Ic {...p}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></Ic>,
  barcode: (p) => <Ic {...p}><path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2" /><path d="M7 8v8M10 8v8M13 8v8M17 8v8" /></Ic>,
  camera: (p) => <Ic {...p}><path d="M14.5 4h-5L7.9 6H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-3.9L14.5 4z" /><circle cx="12" cy="13" r="3.5" /></Ic>,
  plus: (p) => <Ic {...p}><path d="M12 5v14M5 12h14" /></Ic>,
  check: (p) => <Ic {...p}><path d="M4 12.5l5 5L20 6.5" /></Ic>,
  x: (p) => <Ic {...p}><path d="M18 6 6 18M6 6l12 12" /></Ic>,
  back: (p) => <Ic {...p}><path d="M15 18l-6-6 6-6" /></Ic>,
  chev: (p) => <Ic {...p}><path d="M9 18l6-6-6-6" /></Ic>,
  trash: (p) => <Ic {...p}><path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></Ic>,
  pack: (p) => <Ic {...p}><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><path d="M3.3 7l8.7 5 8.7-5" /><path d="M12 22V12" /></Ic>,
  mail: (p) => <Ic {...p}><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" /></Ic>,
  truck: (p) => <Ic {...p}><path d="M14 18V6a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h2" /><path d="M14 9h4l3 3v5a1 1 0 0 1-1 1h-1" /><circle cx="7.5" cy="18" r="2" /><circle cx="16.5" cy="18" r="2" /><path d="M9.5 18H14" /></Ic>,
  wallet: (p) => <Ic {...p}><path d="M19 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2H5" /><circle cx="16" cy="14" r="1.4" fill="currentColor" stroke="none" /></Ic>,
  leaf: (p) => <Ic {...p}><path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" /><path d="M2 21c0-3 1.85-5.36 5.08-6" /></Ic>,
  pencil: (p) => <Ic {...p}><path d="M17 3a2.8 2.8 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5z" /></Ic>,
  store: (p) => <Ic {...p}><path d="M3 9l1.5-5h15L21 9" /><path d="M3 9a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0" /><path d="M5 12v8a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-8" /><path d="M9 21v-5h6v5" /></Ic>,
  user: (p) => <Ic {...p}><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 3.6-6.5 8-6.5s8 2.5 8 6.5" /></Ic>,
  info: (p) => <Ic {...p}><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></Ic>,
  flame: (p) => <Ic {...p}><path d="M12 2c1 4-4 5.5-4 10a4 4 0 0 0 8 .5c1.5 1 2-4.5-1-7.5 0 2.5-1.5 3-1.5 3S13.5 5 12 2z" /></Ic>,
  spinner: (p) => <Ic {...p}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" /><path d="M21 12a9 9 0 0 0-9-9" /></Ic>,
  external: (p) => <Ic {...p}><path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h6" /></Ic>,
  download: (p) => <Ic {...p}><path d="M12 3v12" /><path d="m7 11 5 5 5-5" /><path d="M5 21h14" /></Ic>,
  shield: (p) => <Ic {...p}><path d="M12 2 4 5v6c0 5 3.5 8 8 11 4.5-3 8-6 8-11V5z" /><path d="m9 12 2 2 4-4" /></Ic>,
  heart: (p) => <Ic {...p}><path d="M12 20.5s-7.5-4.6-7.5-10.3A4.2 4.2 0 0 1 12 7.6a4.2 4.2 0 0 1 7.5 2.6c0 5.7-7.5 10.3-7.5 10.3z" /></Ic>,
  lock: (p) => <Ic {...p}><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></Ic>,
};

// ── Coche animée (succès) — trait qui se dessine ──
// Rosace jaune doux (style sticker de la charte) + coche bleu nuit qui se trace.
const LOBES = Array.from({ length: 12 }, (_, i) => {
  const a = (i / 12) * Math.PI * 2;
  return [28 + Math.cos(a) * 17, 28 + Math.sin(a) * 17];
});
export function CocheAnimee({ size = 64 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 56 56" aria-hidden="true" className="sticker pop" style={{ display: 'block' }}>
      <g fill="#F6CF73">
        {LOBES.map(([x, y], i) => <circle key={i} cx={x} cy={y} r="7" />)}
        <circle cx="28" cy="28" r="18" />
      </g>
      <path d="M19 28.5l6 6 12-12.5" fill="none" stroke="#3A3585" strokeWidth="4.2" strokeLinecap="round" strokeLinejoin="round"
        style={{ strokeDasharray: 48, animation: 'proto-check .5s var(--ease) .3s both' }} />
      <path d={etincelle(51, 6, 5)} fill="#F59A9A" />
      <path d={etincelle(5, 47, 3.5)} fill="#F59A9A" />
    </svg>
  );
}

// ── Logo re.rejouis : cœur officiel + mot-symbole ──
export function LogoRe({ size = 18 }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: size * 0.42 }}>
      <CoeurRejouis taille={size * 1.55} />
      <span style={{ fontFamily: 'var(--font-logo)', fontSize: size, textTransform: 'lowercase', letterSpacing: '0.01em', display: 'inline-flex', alignItems: 'baseline', lineHeight: 1 }}>
        <span style={{ color: 'var(--rouge)' }}>re.</span>
        <span style={{ color: 'var(--noir)' }}>rejouis</span>
      </span>
    </span>
  );
}

// ── Vignette produit : image réelle si dispo, sinon placeholder rayé ──
export function Thumb({ produit, size = 52, radius = 12 }) {
  const initiales = (produit.marque || produit.titre || '?').slice(0, 2).toUpperCase();
  if (produit.image) {
    return (
      <img src={produit.image} alt="" width={size} height={size}
        style={{ width: size, height: size, borderRadius: radius, objectFit: 'cover', flexShrink: 0, background: 'var(--creme-fonce)' }} />
    );
  }
  return (
    <div className="ph-img" style={{ width: size, height: size, borderRadius: radius, flexShrink: 0 }}>
      <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: size * 0.3, color: 'var(--stone)', opacity: 0.75 }}>{initiales}</span>
    </div>
  );
}

// ── En-tête d'écran mobile ──
export function EcranHeader({ titre, onBack, droite }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px 8px', minHeight: 52 }}>
      {onBack ? (
        <button onClick={onBack} aria-label="Retour" style={{ width: 38, height: 38, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--blanc)', border: '1px solid var(--bord)' }}>
          <Icons.back size={19} />
        </button>
      ) : null}
      <div style={{ flex: 1, fontWeight: 700, fontSize: 16 }}>{titre}</div>
      {droite || null}
    </div>
  );
}

// ── Pastille « Boosté » : modèle recherché (Prio), crédit majoré temporairement ──
// `boost` = fraction du crédit ajoutée (0,3 = +30 %), cf. boostPrio (src/lib/prio.js).
export function PastilleBoost({ boost, fin, style }) {
  if (!(boost > 0)) return null;
  const jusqua = fin ? ' jusqu’au ' + new Date(fin + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }) : '';
  return (
    <span className="badge badge-boost" style={style}
      title={`Modèle recherché : crédit de reprise boosté de ${Math.round(boost * 100)} %${jusqua}`}>
      <Icons.flame size={11} sw={2.4} /> Boosté
    </span>
  );
}

// ── Montant normal barré, à côté du montant boosté d'un modèle recherché ──
export function PrixBarre({ montant, style }) {
  if (!(montant > 0)) return null;
  return (
    <s aria-label={`au lieu de ${fmtEuro(montant)}`}
      style={{ color: 'var(--stone)', fontWeight: 600, whiteSpace: 'nowrap', textDecorationThickness: 2, ...style }}>
      {fmtEuro(montant)}
    </s>
  );
}

// ── Spinner inline ──
export function Spinner({ size = 16, label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--stone)', fontSize: 13 }}>
      <Icons.spinner size={size} sw={2.4} style={{ animation: 'proto-spin .9s linear infinite' }} />
      {label}
    </span>
  );
}
